import type { RefObject } from 'react';

import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useSuperHubStore } from '@core/store';

import type { SuperHubHashRouteParams } from '@routes/SuperHub/types';
import { SuperHubHashRoutePaths } from '@routes/SuperHub/types';

type ScrollTargetRef = RefObject<HTMLElement | null>;

export function scrollToTop(isSmoothScroll = false, target?: ScrollTargetRef) {
  const el = target?.current;

  if (el && typeof el.scrollTo === 'function') {
    el.scrollTo({
      top: 0,
      left: 0,
      behavior: isSmoothScroll ? 'smooth' : 'auto',
    });
  } else {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: isSmoothScroll ? 'smooth' : 'auto',
    });
  }
}
export default function ScrollTop({
  smooth = false,
  deps,
  target,
}: {
  deps?: string[] | undefined;
  smooth?: boolean;
  target?: ScrollTargetRef;
}) {
  const [isSuperHub, isSuperHubAdminOrEditor, isDocumentLoading] = useSuperHubStore(s => [
    s.isSuperHub,
    s.user.role === 'admin' || s.user.role === 'editor',
    s.document.isLoading,
  ]);

  const { hash, pathname } = useLocation();

  const dependencies = Array.isArray(deps)
    ? [...deps, target, isDocumentLoading]
    : [pathname, hash, target, isDocumentLoading];

  useEffect(() => {
    const scrollTop = target?.current ? target?.current.scrollTop : window.scrollY;
    // check if hash is valid for jumping to a heading
    const isRouterHash = !!matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));
    const hasTargetHash = hash.includes('#') && hash !== '#/' && hash !== '#' && !isRouterHash;

    // When we're in SuperHub admin mode
    if (isSuperHub && isSuperHubAdminOrEditor && !isDocumentLoading) {
      // The SuperHub hash router is always mounted in admin mode, and it will pollute an anchor target hash with the root `/`
      // eg. `#some-heading` becomes `#/some-heading`. So we need to account for that.
      if (hasTargetHash) {
        // If the current hash does not match a valid SuperHub hash route, treat it as an anchor target hash
        const cleanHash = hash.startsWith('#/') ? hash.slice(2) : hash.startsWith('#') ? hash.slice(1) : hash;
        // We can't supply `cleanHash` directly to `querySelector` because it may contain, or begin
        // with, characters that are not valid selectors (eg. `#3-why-do-i-see-a-data-discrepancy`).

        // Defer the selection of the element to the next animation frame to ensure the target is rendered.
        // Without it, we sometimes fail to find the target element in the DOM.
        window.requestAnimationFrame(() => {
          const heading = document.querySelector(`[id="${cleanHash}"]`);
          if (heading) heading.scrollIntoView();
        });
        return; // Don't scroll to top when we're scrolling to a heading
      }

      scrollToTop(smooth, target);
      return;
    }

    // Check for text fragments in URL hash, see below for more reading:
    // https://web.dev/text-fragments
    let textFragmentHash = '';
    try {
      const performanceName = performance.getEntries().find(({ entryType }) => entryType === 'navigation')?.name;

      if (performanceName) {
        textFragmentHash = new URL(performanceName).hash;
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}

    // Don't scroll if the URL contains a hash parameter,
    // or if the window scroll position is already the top
    if (window.location.hash || textFragmentHash || scrollTop === 0) return;
    scrollToTop(smooth, target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return null;
}
